import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BehaviorSubject, Subject, switchMap } from 'rxjs';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxDataGridModule, DxLookupModule } from 'devextreme-angular';
import { NgIf, AsyncPipe } from '@angular/common';
import { showErrorSwal } from '@asamco/utils';
import { DxiColumnModule, DxoFilterRowModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import { MockDataPreview } from '../interfaces/mock-data-preview.interface';
import { MockDataService } from '../services/mock-data.service';
import { MockDataCreateModalComponent } from './mock-data-create-modal.component';
import { LinkTemplateService } from '../services/link-template.service';

@Component({
  selector: 'app-shell-mock-data-tool',
  templateUrl: './mock-data-tool.component.html',
  styleUrls: ['../styles/alert.scss', '../styles/tile-style.scss', './mock-data-tool.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, FormsModule, DxTemplateModule, AsyncPipe, DxLookupModule, DxoSelectionModule, DxoFilterRowModule, DxiColumnModule, DxDataGridModule, MockDataCreateModalComponent]
})
export class MockDataToolComponent implements OnDestroy, OnInit {
  @ViewChild(MockDataCreateModalComponent)
  createModal: MockDataCreateModalComponent;

  fileSelectBox: HTMLInputElement;

  public mockDataPreview$: BehaviorSubject<MockDataPreview[]> = new BehaviorSubject<MockDataPreview[]>(null);

  sourceSystems;
  targetSystems = [{name: 'AL3 Test', id: 'Test'}, {name: 'AL3 UA', id: 'Acceptance'}, {name: 'AL3 Live', id: 'Live'}];

  sourceSystem: string;
  targetSystem: string;

  loading = false;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef, private mockDataService: MockDataService, private linkTemplateService: LinkTemplateService) {
    this.sourceSystems = this.targetSystems;
  }

  ngOnInit() {
    this.linkTemplateService.getAllLinkTemplates().subscribe(lts => this.createModal.linkTemplates = lts.filter(lt => lt.isFrom));

    this.fileSelectBox = <HTMLInputElement>document.getElementById('fileSelect');
    this.fileSelectBox.addEventListener('change', this.completeUpload.bind(this), false);
  }

  onSourceSystemChange($event) {
    this.updatePreview();
  }

  updatePreview() {
    this.mockDataPreview$.next(undefined);

    if (this.sourceSystem) {
      this.loading = true;
      this.mockDataService.getDomainPreview(this.sourceSystem).subscribe({ next: ud => {
          this.loading = false;
          this.mockDataPreview$.next(ud);
        }, error: err => {
          showErrorSwal(err);
          this.loading = false;
        } });
    }
  }

  deleteMockData(mockDataIds: number[]) {
      this.loading = true;

      this.mockDataService.deleteMockData({
        system: this.sourceSystem,
        mockDataIds: mockDataIds
      }).subscribe({
        next: r => {
          this.loading = false;

          Swal.fire({
            icon: 'success',
            title: `Mock data successfully deleted.`
          });
          this.updatePreview();
        }, error: err => {
          showErrorSwal(err);
          this.loading = false;
        }
      });
  }

  transferMockData(mockDataIds: number[]) {
    if(this.sourceSystem && this.targetSystem) {
      this.loading = true;

      this.mockDataService.transferMockData({
        sourceSystem: this.sourceSystem,
        targetSystem: this.targetSystem,
        sourceMockDataIds: mockDataIds
      }).subscribe({
        next: r => {
          this.loading = false;

          const successes = r.map(rs => rs.successes).reduce((a, c) => a + c, 0);
          const total = r.map(rs => rs.successes + rs.failures).reduce((a, c) => a + c, 0);

          Swal.fire({
            icon: successes === total ? 'success' : 'warning',
            title: `MockData transfer completed. Transfering ${successes}/${total} objects succeeded. See the attached file for detailed report.`
          });

          const a = <any>document.getElementById('linkanchor');
          const file = new Blob([JSON.stringify(r)], { type: 'application/json' });
          a.href = URL.createObjectURL(file);
          a.download = 'transferResults.json';
          a.dispatchEvent(new MouseEvent('click'));

          if(this.sourceSystem === this.targetSystem) {
            this.updatePreview();
          }
        }, error: err => {
            showErrorSwal(err);
            this.loading = false;
        }
      });
    }
  }

  downloadMockData(mockDataId: number) {
    this.loading = true;
    this.mockDataService.downloadMockData({
        system: this.sourceSystem,
        mockDataId: mockDataId
      }).subscribe({
        next: r => {
          this.loading = false;

          const a = <any>document.getElementById('linkanchor');
          const file = new Blob([JSON.stringify(r)], { type: 'application/json' });
          a.href = URL.createObjectURL(file);
          a.download = 'mockdata.json';
          a.dispatchEvent(new MouseEvent('click'));
        }, error: err => {
          showErrorSwal(err);
          this.loading = false;
        }
    });
  }

  uploadMockData() {
    this.fileSelectBox.value = '';
    this.fileSelectBox.click();
  }

  completeUpload(event) {
    this.mockDataService.loadedFiles = event.target.files;

    this.loading = true;
    this.createModal.show().pipe(switchMap(uploadModel => {
        uploadModel.system = this.sourceSystem;
        return this.mockDataService.uploadMockData(uploadModel);
      })).subscribe({next: r => {
      Swal.fire({
            icon: 'success',
            title: `MockData upload completed.`
      });
      this.updatePreview();
    }, error: showErrorSwal});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
